import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/seo/Seo";

const RITUALS = [
  {
    slug: "rest",
    overline: "01",
    name: "Rest",
    tagline: "A ritual for slowing down.",
    body:
      "Our first collection of considered rituals for slower evenings, deeper rest, and everyday restoration. Coming December 2026.",
    status: "coming-soon",
    cta: "Join the waitlist",
    tint: "bg-forest text-cream",
    accent: "text-terracotta-light",
    btn: "bg-cream text-forest hover:bg-terracotta hover:text-cream",
    rule: "border-forest/40",
    image: "/generated/a-quiet-evening-still-life-on-a-deep-for-3342bd.png",
    barColor: "#2C3A26",
  },
  {
    slug: "ground",
    overline: "02",
    name: "Ground",
    tagline: "Presence, calm and the sensory everyday.",
    body:
      "Bath, breath and the slow return to the body. In formulation.",
    status: "coming-soon",
    cta: "Notify me",
    tint: "bg-sage/25 text-ink",
    accent: "text-moss-dark",
    btn: "border border-moss-dark text-moss-dark hover:bg-moss-dark hover:text-cream",
    rule: "border-moss/30",
    image: "/generated/a-low-angle-serene-still-life-on-soft-sa-0747e0.png",
    barColor: "#8A9872",
  },
  {
    slug: "restore",
    overline: "03",
    name: "Restore",
    tagline: "Recovery and nourishment, read closely.",
    body:
      "After exertion, after illness, after the long week. In formulation.",
    status: "coming-soon",
    cta: "Notify me",
    tint: "bg-terracotta/18 text-ink",
    accent: "text-terracotta-dark",
    btn: "border border-terracotta-dark text-terracotta-dark hover:bg-terracotta hover:text-cream",
    rule: "border-terracotta/30",
    image: "/generated/a-warm-restorative-still-life-on-a-terra-098495.png",
    barColor: "#B2654A",
  },
  {
    slug: "daily",
    overline: "04",
    name: "Daily",
    tagline: "Morning and evening wellness, considered.",
    body:
      "AM and PM wellness sachets. Composed. Repeated. Everyday.",
    status: "coming-soon",
    cta: "Notify me",
    tint: "bg-ochre/20 text-ink",
    accent: "text-ochre-dark",
    btn: "border border-ochre-dark text-ochre-dark hover:bg-ochre-dark hover:text-cream",
    rule: "border-ochre/40",
    image: "/generated/a-composed-morning-to-evening-still-life-b444bf.png",
    barColor: "#B48837",
  },
];

export default function Rituals() {
  const [barColor, setBarColor] = useState(null);
  return (
    <div data-testid="rituals-page" className="pt-32 md:pt-40">
      {/* Scent sampler bar — fixed to top, tints when a tile is hovered */}
      <div
        aria-hidden
        data-testid="scent-bar"
        className="fixed top-0 left-0 right-0 h-[3px] z-[60] pointer-events-none transition-colors duration-500 ease-out"
        style={{ backgroundColor: barColor || "transparent" }}
      />
      <Seo
        path="/rituals"
        title="The Rituals"
        description="Modern wellness rituals for the body, mind and everyday life. Rest, Ground, Restore and Daily — drawn from botanicals, minerals, and considered formulation."
      />

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <p className="overline mb-6">The Rituals</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[8rem] font-light leading-[0.92] tracking-[-0.03em] text-ink">
              A small vocabulary<br />
              of <em className="italic text-moss">return.</em>
            </h1>
          </div>
          <p className="lg:col-span-4 lg:col-start-9 text-charcoal text-base md:text-lg font-light leading-[1.7]">
            Stillform is organised around rituals, not products. Four rituals, each
            built for a part of the day — some topical, some ingestible, all simple
            enough to repeat.
          </p>
        </div>
      </section>

      {/* Rituals grid — image + text per tile, alternating sides */}
      <section className="border-t border-hairline">
        {RITUALS.map((r, i) => {
          const alt = i % 2 === 1;
          return (
            <article
              key={r.slug}
              data-testid={`ritual-${r.slug}`}
              onMouseEnter={() => setBarColor(r.barColor)}
              onMouseLeave={() => setBarColor(null)}
              onFocus={() => setBarColor(r.barColor)}
              onBlur={() => setBarColor(null)}
              className={`border-b ${r.rule} ${r.tint}`}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-12 items-stretch ${alt ? "lg:[&>*:first-child]:order-2" : ""}`}>
                {/* Image */}
                <div className="lg:col-span-6 relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:min-h-[560px]">
                  <img
                    src={r.image}
                    alt={`${r.name} ritual`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Copy */}
                <div className="lg:col-span-6 px-6 md:px-12 lg:px-16 py-16 md:py-24 flex flex-col justify-center">
                  <div className="flex items-center gap-6 mb-6">
                    <p className={`font-mono text-sm ${r.accent}`}>{r.overline}</p>
                    <span className="h-px flex-1 bg-current opacity-20 max-w-[80px]" />
                    <p className={`overline ${r.accent}`}>
                      {r.slug === "rest" ? "Coming Dec 2026" : "Coming soon"}
                    </p>
                  </div>

                  <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] tracking-[-0.02em]">
                    {r.name}.
                  </h2>
                  <p className="mt-6 font-display italic text-xl md:text-2xl opacity-85 max-w-xl leading-snug">
                    {r.tagline}
                  </p>
                  <p className="mt-6 font-light leading-[1.75] max-w-md opacity-85">
                    {r.body}
                  </p>

                  <Link
                    to={`/rituals/${r.slug}`}
                    data-testid={`ritual-cta-${r.slug}`}
                    className={`mt-10 inline-flex self-start items-center gap-2 text-[0.72rem] tracking-[0.24em] uppercase px-6 py-3 transition-colors ${r.btn}`}
                  >
                    {r.cta} <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}
